import { Link, useNavigate } from 'react-router-dom';
import { routeBase, studyName, studyThumbnail, useCollectionMutations, useOpenStudy } from '../api/collections';
import { imageThumbnail, useHome } from '../api/explore';
import { imageTag } from '../../data/imageTypes';
import { Cover, MosaicCard, StackThumb } from '../ui/Cards';
import { IconHeart } from '../ui/icons';
import { plainText } from '../ui/RichText';
import { fromServerVisibility } from '../ui/Visibility';

/**
 * Home (`is.home`): "What's new in {workspace}". Recent collections (four
 * Mosaic Cards, See all), recent studies (six Stack Thumbs), recent uploads
 * (six image tiles, this week). No Browse Bar here (PRODUCT_RULES).
 */
export function HomePage() {
  const nav = useNavigate();
  const { data } = useHome();
  const m = useCollectionMutations();
  const openStudy = useOpenStudy();
  if (!data) return <div style={{ flex: 1 }} />;
  const section = (title: string, sub: string | null, children: React.ReactNode, right?: React.ReactNode, first?: boolean) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: first ? 0 : 28, borderTop: first ? undefined : '1px solid #ebebea' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h2>
          {sub ? <span style={{ fontSize: 13, color: '#8a8a8f' }}>{sub}</span> : null}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
  return (
    <div className="tr-page" style={{ padding: '36px 28px 44px', display: 'flex', flexDirection: 'column', gap: 36, maxWidth: 1440, width: '100%', margin: '0 auto', flex: 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, letterSpacing: '-.02em' }}>Home</h1>
        <p style={{ margin: 0, fontSize: 14, color: '#6b6b70' }}>What's new in {data.workspace}</p>
      </div>
      {section(
        'Recent collections',
        `${data.collectionCount} in this workspace`,
        <div className="tr-cols-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '24px 20px' }}>
          {data.recentCollections.map((c) => (
            <MosaicCard
              key={c.public_id}
              c1={c.covers[0] ? studyThumbnail(c.covers[0]) : undefined}
              c2={c.covers[1] ? studyThumbnail(c.covers[1]) : undefined}
              c3={c.covers[2] ? studyThumbnail(c.covers[2]) : undefined}
              count={c.studies}
              vis={fromServerVisibility(c.visibility)}
              title={c.name}
              desc={plainText(c.description)}
              meta={c.author ?? ''}
              likes={c.like_count}
              liked={c.liked}
              discussion={c.comment_count}
              onOpen={() => nav(`${routeBase()}/collections/${c.public_id}`)}
              onToggleLike={() => m.like.mutate({ kind: 'collection', id: c.public_id, liked: c.liked })}
            />
          ))}
        </div>,
        <Link to={`${routeBase()}/collections`} style={{ fontSize: 13, color: '#6b6b70', textDecoration: 'none' }}>See all →</Link>,
        true,
      )}
      {section(
        'Recent studies',
        null,
        <div className="tr-cols-6" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 18 }}>
          {data.recentStudies.map((s) => (
            <div key={s.public_id} onClick={() => openStudy(s.public_id)} style={{ display: 'flex', flexDirection: 'column', gap: 10, cursor: 'pointer' }}>
              <StackThumb cover={studyThumbnail(s.public_id)} count={s.images} vis={fromServerVisibility(s.visibility_own)} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, fontFamily: "'JetBrains Mono', monospace" }}>{studyName(s)}</div>
                  <div className="tr-clamp-2" style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.4 }}>{s.description ?? ''}</div>
                  <div className="tr-ellipsis" style={{ fontSize: 12, color: '#8a8a8f' }}>{[s.parent, new Date(s.created_utc).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })].filter(Boolean).join(' · ')}</div>
                </div>
                <div onClick={(e) => { e.stopPropagation(); m.like.mutate({ kind: 'study', id: s.public_id, liked: s.liked }); }} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: '#6b6b70', paddingTop: 2 }}>
                  <IconHeart size={13} fill={s.liked ? '#d9342b' : 'none'} color={s.liked ? '#d9342b' : 'currentColor'} />
                  {s.like_count}
                </div>
              </div>
            </div>
          ))}
        </div>,
      )}
      {section(
        'Recent uploads',
        'images imported this week',
        <div className="tr-cols-6" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 18 }}>
          {data.recentUploads.map((u) => (
            <div key={u.image_id} onClick={() => openStudy(u.study, u.series_instance_uid)} style={{ display: 'flex', flexDirection: 'column', gap: 10, cursor: 'pointer' }}>
              <div className="tr-hover-ring" style={{ position: 'relative', aspectRatio: '4 / 3', borderRadius: 10, border: '1px solid #ebebea', background: '#e8e8e6', overflow: 'hidden' }}>
                <Cover src={imageThumbnail(u.image_id)} fallback="#e8e8e6" style={{ position: 'absolute', inset: 0 }} />
                <div style={{ position: 'absolute', bottom: 8, left: 8, height: 20, padding: '0 7px', borderRadius: 5, background: 'rgba(255,255,255,.9)', fontSize: 10.5, fontWeight: 500, lineHeight: '20px', fontFamily: "'JetBrains Mono', monospace" }}>{imageTag(u.kind, u.image_type)}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                <div className="tr-ellipsis" style={{ fontSize: 13.5, fontWeight: 500 }}>{u.original_filename || u.description || u.image_id.slice(-8)}</div>
                <div style={{ fontSize: 12, color: '#6b6b70' }}>{u.study_id ?? ''}</div>
              </div>
            </div>
          ))}
        </div>,
      )}
    </div>
  );
}
